import React, { useCallback, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
	EmailWorkflowApi,
	CreateWorkflowBody,
	CounterpartyRow,
	RoutingResult,
	validateProofFile,
} from '../../services/emailWorkflowApi';
import { ProofFileInput } from './ProofFileInput';
import { AsyncAutocomplete } from '../../components/ui/AsyncAutocomplete';
import { formatINR } from '../../utils/currencyFormatter';

/**
 * The New Request FORM — rendered inside the board's modal (no page chrome).
 * Two steps (PR #209): POST / creates + auto-routes, then POST /:threadId/proof-file
 * uploads the actual invoice (multipart). Limits mirrored client-side: 15 MB,
 * .pdf .docx .jpg .jpeg .png .webp — the server enforces the same with 400s.
 * On success it calls onRaised(created) — the board owns the toast + listing focus.
 */

const DRAFT_KEY = 'email-workflow-new-draft';

export interface RaisedResult {
	thread_id: string;
	routing: RoutingResult | null;
}

export const NewRequestForm: React.FC<{ onRaised: (r: RaisedResult) => void }> = ({ onRaised }) => {
	// Draft survives refresh (sessionStorage — same tab only). The picked FILE cannot:
	// browsers forbid persisting it, so after a restore the user re-attaches.
	const [form, setForm] = useState<Omit<CreateWorkflowBody, 'proof' | 'amount'> & { amount: string }>(() => {
		const empty = {
			workflow_type: 'vendor_payment' as const,
			counterparty: '',
			counterparty_kind: 'company' as const,
			amount: '',
			purpose: '',
			note: '',
		};
		try {
			const saved = sessionStorage.getItem(DRAFT_KEY);
			return saved ? { ...empty, ...JSON.parse(saved) } : empty;
		} catch {
			return empty;
		}
	});
	const [proofFile, setProofFile] = useState<File | null>(null);
	const [fileError, setFileError] = useState<string | null>(null);
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);
	// If create succeeded but the upload failed, remember the thread so a resubmit
	// only retries the upload instead of raising a duplicate request.
	const createdRef = useRef<RaisedResult | null>(null);
	// Canonical counterparty picker (spec: payment-counterparty-picker). The server
	// groups by the raiser's city — city entries carry a "— your city" marker. A picked
	// entry carries its canonical id; free-typed text (allowCreate) leaves id null and
	// the server does create-if-new on raise.
	const [counterpartyId, setCounterpartyId] = useState<number | null>(null);
	const [purposeOptions, setPurposeOptions] = useState<string[]>([]);
	const searchCounterparties = useCallback(async (q: string) => {
		try {
			const res = await EmailWorkflowApi.getCounterparties(q.trim());
			return [
				...res.groups.mine.map(c => ({ label: `${c.name} — your city`, value: c })),
				...res.groups.other.map(c => ({ label: c.name, value: c })),
			];
		} catch {
			return []; // suggestions are optional — typing still works
		}
	}, []);
	const pickCounterparty = (item: string | { label: string; value: any }) => {
		if (typeof item === 'string') {
			// free-typed new payee (the "+ Add" footer)
			setCounterpartyId(null);
			setPurposeOptions([]);
			set({ counterparty: item });
			return;
		}
		const c = item.value as CounterpartyRow;
		setCounterpartyId(c.id);
		set({ counterparty: c.name, counterparty_kind: c.kind });
		EmailWorkflowApi.getCounterpartyPurposes(c.id)
			.then(p => {
				setPurposeOptions(p.purposes);
				// one-vendor-one-purpose is the common case — prefill, stays editable
				if (p.purposes[0]) set({ purpose: p.purposes[0] });
			})
			.catch(() => setPurposeOptions([]));
	};

	const set = (patch: Partial<typeof form>) =>
		setForm(f => {
			const next = { ...f, ...patch };
			try {
				sessionStorage.setItem(DRAFT_KEY, JSON.stringify(next));
			} catch {
				/* storage full/blocked — draft just won't survive refresh */
			}
			return next;
		});

	const pickFile = (file: File | null) => {
		setFileError(null);
		if (!file) {
			setProofFile(null);
			return;
		}
		const err = validateProofFile(file);
		if (err) {
			setProofFile(null);
			setFileError(err);
			return;
		}
		setProofFile(file);
	};

	const amountNum = Number(form.amount);
	const valid =
		form.counterparty.trim() !== '' &&
		form.purpose.trim() !== '' &&
		Number.isFinite(amountNum) &&
		amountNum > 0 &&
		proofFile != null;

	// name what still blocks the button, so "disabled" never reads as broken
	const missing = [
		form.counterparty.trim() === '' && 'vendor name',
		!(Number.isFinite(amountNum) && amountNum > 0) && 'amount',
		form.purpose.trim() === '' && 'purpose',
		proofFile == null && 'the invoice file',
	].filter(Boolean) as string[];

	const submit = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!valid || submitting || !proofFile) return;
		setSubmitting(true);
		setError(null);
		try {
			let created = createdRef.current;
			if (!created) {
				const res = await EmailWorkflowApi.create({
					workflow_type: form.workflow_type,
					counterparty: form.counterparty.trim(),
					counterparty_kind: form.counterparty_kind,
					counterparty_id: counterpartyId ?? undefined,
					amount: amountNum,
					purpose: form.purpose.trim(),
					note: form.note?.trim() || undefined,
					proof: { source: 'dashboard_upload', filename: proofFile.name },
				});
				created = { thread_id: res.thread_id, routing: res.routing };
				createdRef.current = created;
			}
			await EmailWorkflowApi.uploadProofFile(created.thread_id, proofFile);
			sessionStorage.removeItem(DRAFT_KEY); // raised — the draft is done
			createdRef.current = null; // fresh slate if the modal is reopened
			onRaised(created);
		} catch (err) {
			const msg = (err as { message?: string })?.message;
			setError(
				createdRef.current
					? `The request was raised, but the file didn't upload${msg ? ` (${msg})` : ''}. Press "Raise request" again to retry the upload.`
					: msg || 'Could not create the request.'
			);
		} finally {
			setSubmitting(false);
		}
	};

	const inputCls = 'w-full border border-gray-200 rounded-md px-3 py-2 text-sm';

	return (
		<form onSubmit={submit} className="space-y-4">
			<div>
				<label className="text-xs text-gray-500">Type</label>
				<select value={form.workflow_type} onChange={e => set({ workflow_type: e.target.value as never })} className={inputCls}>
					<option value="vendor_payment">Vendor payment</option>
					<option value="reimbursement">Reimbursement</option>
				</select>
			</div>

			<div>
				<AsyncAutocomplete
					label="Vendor name"
					required
					value={form.counterparty}
					onChange={v => { setCounterpartyId(null); set({ counterparty: v }); }}
					onSelect={pickCounterparty}
					placeholder=""
					searchFn={searchCounterparties}
					minChars={1}
					allowCreate
				/>
			</div>

			<div>
				<label className="text-xs text-gray-500">Amount (₹)</label>
				<input type="number" min="1" step="0.01" value={form.amount} onChange={e => set({ amount: e.target.value })} placeholder="8000" className={inputCls} />
				{form.amount && amountNum > 0 && <p className="text-xs text-gray-400 mt-1">{formatINR(amountNum)}</p>}
			</div>

			<div>
				<label className="text-xs text-gray-500">Purpose</label>
				<input
					value={form.purpose}
					onChange={e => set({ purpose: e.target.value })}
					placeholder="April pest control"
					className={inputCls}
					list="purpose-suggestions"
				/>
				{/* native datalist — this payee's recent purposes, self-learning from history */}
				<datalist id="purpose-suggestions">
					{purposeOptions.map(p => <option key={p} value={p} />)}
				</datalist>
			</div>

			<div>
				<label className="text-xs text-gray-500">Note (optional)</label>
				<textarea value={form.note} onChange={e => set({ note: e.target.value })} rows={2} className={inputCls} />
			</div>

			<div>
				<label className="text-xs text-gray-500">Invoice / proof (required)</label>
				<div className="mt-1">
					<ProofFileInput
						onFile={pickFile}
						fileName={
							proofFile
								? `${proofFile.name} (${(proofFile.size / (1024 * 1024)).toFixed(1)} MB)`
								: null
						}
					/>
				</div>
				{fileError && <p className="text-xs text-red-600 mt-1">{fileError}</p>}
				<p className="text-xs text-gray-400 mt-1">PDF, DOCX, JPG, PNG or WebP — up to 15 MB.</p>
			</div>

			{error && <p className="text-sm text-red-600">{error}</p>}

			<button
				type="submit"
				disabled={!valid || submitting}
				className="w-full px-4 py-2 rounded-md bg-primary text-white text-sm font-medium disabled:opacity-50 inline-flex items-center justify-center gap-1"
			>
				{submitting && <Loader2 className="h-4 w-4 animate-spin" />} Raise request
			</button>
			{!valid && missing.length > 0 && (
				<p className="text-xs text-gray-400 text-center">Still needed: {missing.join(', ')}.</p>
			)}
		</form>
	);
};
